import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    bomberosKPIAPI,
    bomberosCompaniaAPI,
    bomberosSeedAPI,
    bomberosAlertaAPI,
    bomberosSolicitudesBajaAPI,
    bomberosDashboardAPI,
    bomberosMaterialMenorAPI,
} from '../../lib/api';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Users, Truck, GraduationCap, HardHat, AlertTriangle,
    ChevronRight, Building2, Flame, Plus, ArrowLeft, Settings,
    Bell, RefreshCw, FileText, Upload, PieChart, FileCheck,
    CalendarDays, EyeOff, LayoutDashboard, TrendingUp,
    Award, BarChart3, Download, Filter, ShieldAlert,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- Constantes ---
const WIDGET_DEFS = [
    { id: 'kpis', label: 'KPIs Generales', icon: TrendingUp, defaultOn: true },
    { id: 'personal_cia', label: 'Personal por Compañía', icon: Users, defaultOn: true },
    { id: 'asistencia_cap', label: '% Asistencia Capacitaciones', icon: GraduationCap, defaultOn: true },
    { id: 'asistencia_actas', label: '% Asistencia Actas/Actividades', icon: FileText, defaultOn: true },
    { id: 'epp_estado', label: 'EPP por Estado', icon: HardHat, defaultOn: true },
    { id: 'epp_vencimientos', label: 'Vencimientos EPP', icon: ShieldAlert, defaultOn: true },
    { id: 'epp_cia', label: 'EPP por Compañía', icon: HardHat, defaultOn: false },
    { id: 'material_mayor', label: 'Material Mayor por Compañía', icon: Truck, defaultOn: false },
    { id: 'caps_estado', label: 'Capacitaciones por Estado', icon: GraduationCap, defaultOn: false },
    { id: 'competencias_cia', label: 'Cobertura Competencias', icon: Award, defaultOn: false },
    { id: 'companias', label: 'Compañías', icon: Building2, defaultOn: true },
    { id: 'acciones', label: 'Acciones Rápidas', icon: Plus, defaultOn: true },
];

const GRADO_CONFIG = {
    comandante: { color: 'bg-red-500', label: 'Cmd' },
    capitan:    { color: 'bg-orange-500', label: 'Cap' },
    teniente:   { color: 'bg-yellow-500', label: 'Tte' },
    bombero:    { color: 'bg-blue-500', label: 'Bro' },
    voluntario: { color: 'bg-green-500', label: 'Vol' },
};

const GRADO_ORDER = ['comandante', 'capitan', 'teniente', 'bombero', 'voluntario'];

// --- Componentes auxiliares ---
function StatCard({ title, value, subtitle, icon: Icon, color, onClick }) {
    return (
        <Card className="border-2 hover:border-primary/50 transition-colors cursor-pointer" onClick={onClick}
            data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
                        <p className="text-2xl font-bold mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                    </div>
                    <div className={`p-2.5 rounded-lg ${color}`}>
                        <Icon className="h-5 w-5" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function GradoStackedBar({ cia }) {
    const grados = cia.por_grado || {};
    const total = cia.total || 0;
    if (total === 0) return <div className="text-xs text-muted-foreground">Sin personal</div>;

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground truncate w-40">{cia.compania_nombre}</span>
                <span className="font-mono font-medium text-xs">{cia.activos} activos / {total} total</span>
            </div>
            <div className="flex h-6 rounded-full overflow-hidden bg-muted">
                {GRADO_ORDER.map(grado => {
                    const count = grados[grado] || 0;
                    if (count === 0) return null;
                    const pct = (count / total) * 100;
                    return (
                        <div
                            key={grado}
                            className={`${GRADO_CONFIG[grado]?.color || 'bg-gray-400'} h-full transition-all duration-500 relative group`}
                            style={{ width: `${pct}%` }}
                            title={`${grado}: ${count}`}
                        >
                            {pct >= 12 && (
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                                    {count}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function WidgetExportBtn({ onExport }) {
    return (
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onExport} title="Exportar widget" data-testid="widget-export-btn">
            <Download className="h-3.5 w-3.5" />
        </Button>
    );
}

// --- Funciones de exportación ---
function buildAllSheetsData(analytics, companias, kpis, filterCia) {
    const sheets = [];
    const totales = analytics?.totales || {};
    const label = filterCia ? `(Filtro: ${companias.find(c => c.id === filterCia)?.nombre || filterCia})` : '(Todas las compañías)';

    // Hoja 1: Resumen
    sheets.push({
        name: 'Resumen',
        data: [
            ['Métrica', 'Valor'],
            ['Personal Total', totales.personal_total],
            ['Personal Activo', totales.personal_activo],
            ['Compañías', totales.companias],
            ['Capacitaciones', totales.capacitaciones_total],
            ['EPP Total', totales.epp_total],
            ['Material Mayor', totales.material_mayor_total],
            ['Asistencia General %', totales.asistencia_general_porcentaje],
            ['Filtro', label],
        ],
    });

    // Hoja 2: Personal por Compañía
    if (analytics?.personal_por_compania) {
        const rows = [['Compañía', 'Total', 'Activos', 'Inactivos', ...GRADO_ORDER.map(g => g.charAt(0).toUpperCase() + g.slice(1))]];
        for (const cia of analytics.personal_por_compania) {
            rows.push([
                cia.compania_nombre, cia.total, cia.activos, cia.inactivos,
                ...GRADO_ORDER.map(g => cia.por_grado?.[g] || 0),
            ]);
        }
        sheets.push({ name: 'Personal x Compañía', data: rows });
    }

    // Hoja 3: Asistencia Capacitaciones
    if (analytics?.asistencia_capacitaciones_por_compania) {
        const rows = [['Compañía', 'Registros', 'Presentes', '% Asistencia']];
        for (const cia of analytics.asistencia_capacitaciones_por_compania) {
            rows.push([cia.compania_nombre, cia.registros_asistencia, cia.presentes, cia.porcentaje]);
        }
        sheets.push({ name: 'Asist. Capacitaciones', data: rows });
    }

    // Hoja 4: Asistencia Actas
    if (analytics?.asistencia_actas_por_compania) {
        const rows = [['Compañía', 'Total Actas', 'Posibles', 'Presentes', '% Asistencia']];
        for (const cia of analytics.asistencia_actas_por_compania) {
            rows.push([cia.compania_nombre, cia.total_actas, cia.total_posibles, cia.total_presentes, cia.porcentaje]);
        }
        sheets.push({ name: 'Asist. Actas', data: rows });
    }

    // Hoja 5: EPP por Compañía
    if (analytics?.epp_por_compania) {
        const rows = [['Compañía', 'Total', 'Asignado', 'Disponible', 'Mantenimiento', 'Baja']];
        for (const cia of analytics.epp_por_compania) {
            rows.push([cia.compania_nombre, cia.total, cia.asignado, cia.disponible, cia.mantenimiento, cia.baja]);
        }
        sheets.push({ name: 'EPP x Compañía', data: rows });
    }

    // Hoja 6: Material Mayor
    if (analytics?.material_mayor_por_compania) {
        const rows = [['Compañía', 'Total', 'Operativos', '% Operatividad']];
        for (const cia of analytics.material_mayor_por_compania) {
            rows.push([cia.compania_nombre, cia.total, cia.operativos, cia.porcentaje_operatividad]);
        }
        sheets.push({ name: 'Material Mayor', data: rows });
    }

    // Hoja 7: Competencias
    if (analytics?.competencias_por_compania) {
        const rows = [['Compañía', 'Bomberos Activos', 'Con Competencias', 'Sin Competencias', '% Cobertura']];
        for (const cia of analytics.competencias_por_compania) {
            rows.push([cia.compania_nombre, cia.bomberos_activos, cia.con_competencias, cia.sin_competencias, cia.porcentaje_cobertura]);
        }
        sheets.push({ name: 'Competencias', data: rows });
    }

    return sheets;
}

function exportXLSX(sheets) {
    const wb = XLSX.utils.book_new();
    for (const sheet of sheets) {
        const ws = XLSX.utils.aoa_to_sheet(sheet.data);
        // Auto-width columns
        const colWidths = sheet.data[0].map((_, ci) =>
            Math.max(...sheet.data.map(row => String(row[ci] ?? '').length)) + 2
        );
        ws['!cols'] = colWidths.map(w => ({ wch: w }));
        XLSX.utils.book_append_sheet(wb, ws, sheet.name.substring(0, 31));
    }
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `bomberos_dashboard_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function exportCSV(sheets) {
    // Concatenate all sheets into one CSV
    let csv = '';
    for (const sheet of sheets) {
        csv += `\n--- ${sheet.name} ---\n`;
        for (const row of sheet.data) {
            csv += row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',') + '\n';
        }
    }
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `bomberos_dashboard_${new Date().toISOString().slice(0, 10)}.csv`);
}

function exportPDF(sheets) {
    const doc = new jsPDF({ orientation: 'landscape' });
    const fecha = new Date().toLocaleDateString('es-CL');

    doc.setFontSize(16);
    doc.text('Dashboard Bomberos', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generado: ${fecha}`, 14, 28);

    let startY = 35;
    for (let i = 0; i < sheets.length; i++) {
        const sheet = sheets[i];
        if (i > 0) {
            doc.addPage();
            startY = 20;
        }
        doc.setFontSize(12);
        doc.text(sheet.name, 14, startY);
        startY += 5;

        autoTable(doc, {
            startY,
            head: [sheet.data[0]],
            body: sheet.data.slice(1),
            theme: 'grid',
            headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 10;
    }

    doc.save(`bomberos_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`);
}

function exportSingleWidget(title, headerRow, dataRows) {
    const sheets = [{ name: title, data: [headerRow, ...dataRows] }];
    exportXLSX(sheets);
}

// --- Componente principal ---
export default function BomberosDashboardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const [kpis, setKpis] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [companias, setCompanias] = useState([]);
    const [alertasPendientes, setAlertasPendientes] = useState(0);
    const [solicitudesBajaPendientes, setSolicitudesBajaPendientes] = useState(0);
    const [filterCia, setFilterCia] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [eppVencimientos, setEppVencimientos] = useState(null);
    const [visibleWidgets, setVisibleWidgets] = useState(() => {
        try {
            const saved = localStorage.getItem('bomberos_dash_widgets');
            if (saved) return JSON.parse(saved);
        } catch (e) { /* ignore */ }
        return WIDGET_DEFS.reduce((acc, w) => ({ ...acc, [w.id]: w.defaultOn }), {});
    });

    const toggleWidget = useCallback((id) => {
        setVisibleWidgets(prev => {
            const next = { ...prev, [id]: !prev[id] };
            localStorage.setItem('bomberos_dash_widgets', JSON.stringify(next));
            return next;
        });
    }, []);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [kpisRes, companiasRes, alertasRes, solicitudesRes, analyticsRes, vencRes] = await Promise.all([
                bomberosKPIAPI.get(),
                bomberosCompaniaAPI.getAll(),
                bomberosAlertaAPI.getAll({ resuelta: false }),
                bomberosSolicitudesBajaAPI.getAll('pendiente'),
                bomberosDashboardAPI.getAnalytics().catch(() => ({ data: null })),
                bomberosMaterialMenorAPI.getVencimientos().catch(() => ({ data: null })),
            ]);
            setKpis(kpisRes.data);
            // Sort companias: by numero ascending, null/undefined at end
            const sortedCompanias = [...(companiasRes.data || [])].sort((a, b) => {
                const na = a.numero ?? 9999;
                const nb = b.numero ?? 9999;
                return na - nb;
            });
            setCompanias(sortedCompanias);
            setAlertasPendientes(alertasRes.data.length);
            setSolicitudesBajaPendientes(solicitudesRes.data.length);
            setAnalytics(analyticsRes.data);
            setEppVencimientos(vencRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSeed = async () => {
        setSeeding(true);
        try {
            await bomberosSeedAPI.seed();
            toast.success('Datos de demostración creados');
            loadData();
        } catch (err) {
            if (err.response?.data?.message?.includes('ya existen')) {
                toast.info('Los datos ya existen');
            } else {
                toast.error('Error al crear datos');
            }
        } finally {
            setSeeding(false);
        }
    };

    // --- Filtrar analytics por compañía ---
    const filtered = useMemo(() => {
        if (!analytics) return null;
        if (!filterCia) return analytics;
        const byCia = (arr) => arr?.filter(c => c.compania_id === filterCia) || [];
        return {
            ...analytics,
            personal_por_compania: byCia(analytics.personal_por_compania),
            asistencia_capacitaciones_por_compania: byCia(analytics.asistencia_capacitaciones_por_compania),
            asistencia_actas_por_compania: byCia(analytics.asistencia_actas_por_compania),
            epp_por_compania: byCia(analytics.epp_por_compania),
            material_mayor_por_compania: byCia(analytics.material_mayor_por_compania),
            competencias_por_compania: byCia(analytics.competencias_por_compania),
        };
    }, [analytics, filterCia]);

    const isVisible = (id) => visibleWidgets[id] !== false;
    const totales = filtered?.totales || {};

    // --- Exportaciones ---
    const handleExport = (format) => {
        setShowExportMenu(false);
        const sheets = buildAllSheetsData(filtered, companias, kpis, filterCia);
        if (format === 'xlsx') exportXLSX(sheets);
        else if (format === 'csv') exportCSV(sheets);
        else if (format === 'pdf') exportPDF(sheets);
        toast.success(`Exportado como ${format.toUpperCase()}`);
    };

    if (loading) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-6 p-6 pt-20 md:pt-20">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Skeleton className="h-64" /><Skeleton className="h-64" />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-4 md:space-y-5 p-4 md:p-6 pt-20 md:pt-20">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                                <Flame className="h-5 w-5 md:h-6 md:w-6 text-orange-500" />
                                Gestión de Bomberos
                            </h1>
                            <p className="text-sm text-muted-foreground">Panel de control y métricas institucionales</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant={showConfig ? "default" : "outline"} size="sm" onClick={() => setShowConfig(!showConfig)}
                            data-testid="toggle-config-btn" className="text-xs md:text-sm">
                            {showConfig ? <EyeOff className="h-4 w-4 mr-1" /> : <LayoutDashboard className="h-4 w-4 mr-1" />}
                            {showConfig ? 'Cerrar' : 'Personalizar'}
                        </Button>
                        {/* Exportar */}
                        <div className="relative">
                            <Button variant="outline" size="sm" onClick={() => setShowExportMenu(!showExportMenu)}
                                data-testid="export-menu-btn" className="text-xs md:text-sm">
                                <Download className="h-4 w-4 mr-1" />
                                Exportar
                            </Button>
                            {showExportMenu && (
                                <div className="absolute right-0 top-full mt-1 z-50 bg-background border rounded-lg shadow-lg py-1 min-w-[140px]">
                                    <button className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                        onClick={() => handleExport('xlsx')} data-testid="export-xlsx-btn">
                                        <FileText className="h-4 w-4 text-green-600" /> Excel (.xlsx)
                                    </button>
                                    <button className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                        onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
                                        <FileText className="h-4 w-4 text-red-600" /> PDF
                                    </button>
                                    <button className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                        onClick={() => handleExport('csv')} data-testid="export-csv-btn">
                                        <FileText className="h-4 w-4 text-blue-600" /> CSV
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button variant="default" size="sm" onClick={() => navigate('/bomberos/estadisticas')} data-testid="estadisticas-btn" className="text-xs md:text-sm bg-indigo-600 hover:bg-indigo-700">
                            <PieChart className="h-4 w-4 mr-1" />Estadísticas
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/importacion')} data-testid="import-btn" className="text-xs md:text-sm">
                            <Upload className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Importar</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/actas')} data-testid="actas-btn" className="text-xs md:text-sm">
                            <FileText className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Actas</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/actividades')} data-testid="actividades-btn" className="text-xs md:text-sm">
                            <CalendarDays className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Actividades</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/configuracion')} data-testid="config-btn" className="text-xs md:text-sm">
                            <Settings className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Config</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/alertas')} className="relative text-xs md:text-sm" data-testid="alertas-btn">
                            <Bell className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Alertas</span>
                            {alertasPendientes > 0 && (
                                <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-red-500">{alertasPendientes}</Badge>
                            )}
                        </Button>
                        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
                        {(!kpis || kpis.total_bomberos === 0) && (
                            <Button onClick={handleSeed} disabled={seeding} data-testid="seed-btn">{seeding ? 'Creando...' : 'Crear Datos Demo'}</Button>
                        )}
                    </div>
                </div>

                {/* Filtro compañía */}
                <div className="flex items-center gap-3 flex-wrap" data-testid="filter-cia-section">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={filterCia || '__all__'} onValueChange={(v) => setFilterCia(v === '__all__' ? null : v)}>
                        <SelectTrigger className="w-72" data-testid="filter-cia-select">
                            <SelectValue placeholder="Todas las compañías" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Todas las compañías</SelectItem>
                            {companias.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.numero}a Cía - {c.nombre}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {filterCia && (
                        <Button variant="ghost" size="sm" onClick={() => setFilterCia(null)} data-testid="clear-filter-btn">
                            Limpiar filtro
                        </Button>
                    )}
                </div>

                {/* Panel de configuración */}
                {showConfig && (
                    <Card className="border-2 border-dashed border-primary/30 bg-primary/5" data-testid="widget-config-panel">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <LayoutDashboard className="h-4 w-4" /> Personalizar Dashboard
                            </CardTitle>
                            <CardDescription>Activa o desactiva los widgets que quieres ver</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {WIDGET_DEFS.map(w => (
                                    <div key={w.id} className="flex items-center gap-2 p-2 rounded-lg border bg-background cursor-pointer hover:bg-muted/50"
                                        onClick={() => toggleWidget(w.id)} data-testid={`toggle-widget-${w.id}`}>
                                        <Switch checked={isVisible(w.id)} onCheckedChange={() => toggleWidget(w.id)} />
                                        <w.icon className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-xs font-medium">{w.label}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* KPIs */}
                {isVisible('kpis') && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis-section">
                        <StatCard title="Personal" value={kpis?.bomberos_activos || totales.personal_activo || 0}
                            subtitle={`de ${kpis?.total_bomberos || totales.personal_total || 0} registrados`}
                            icon={Users} color="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400"
                            onClick={() => navigate('/bomberos/personal')} />
                        <StatCard title="Material Mayor" value={`${kpis?.material_mayor_operativo || 0}/${kpis?.material_mayor_total || 0}`}
                            subtitle={`${kpis?.porcentaje_operatividad_mayor || 0}% operativo`}
                            icon={Truck} color="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400"
                            onClick={() => navigate('/bomberos/material-mayor')} />
                        <StatCard title="Académico" value={`${kpis?.cursos_realizados || 0}/${kpis?.cursos_programados || 0}`}
                            subtitle={`${kpis?.porcentaje_academico || 0}% completado`}
                            icon={GraduationCap} color="bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400"
                            onClick={() => navigate('/bomberos/capacitaciones')} />
                        <StatCard title="EPP" value={`${kpis?.material_menor_disponible || 0}/${kpis?.material_menor_total || 0}`}
                            subtitle={`${kpis?.porcentaje_operatividad_menor || 0}% operativo`}
                            icon={HardHat} color="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400"
                            onClick={() => navigate('/bomberos/epp')} />
                    </div>
                )}

                {/* Grid dinámico */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                    {/* Personal por Compañía (con Grados Visuales) */}
                    {isVisible('personal_cia') && filtered?.personal_por_compania && (
                        <Card className="border-2" data-testid="widget-personal-cia">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Users className="h-4 w-4" /> Personal por Compañía
                                    </CardTitle>
                                    <div className="flex items-center gap-1">
                                        <WidgetExportBtn onExport={() => exportSingleWidget('Personal',
                                            ['Compañía','Total','Activos','Inactivos',...GRADO_ORDER],
                                            filtered.personal_por_compania.map(c => [c.compania_nombre,c.total,c.activos,c.inactivos,...GRADO_ORDER.map(g=>c.por_grado?.[g]||0)])
                                        )} />
                                        <Button variant="ghost" size="sm" onClick={() => navigate('/bomberos/personal')}>
                                            Ver todos <ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {filtered.personal_por_compania.map(cia => (
                                    <GradoStackedBar key={cia.compania_id} cia={cia} />
                                ))}
                                {/* Leyenda */}
                                <div className="flex flex-wrap gap-3 pt-3 border-t">
                                    {GRADO_ORDER.map(g => (
                                        <div key={g} className="flex items-center gap-1.5 text-xs">
                                            <div className={`w-3 h-3 rounded-sm ${GRADO_CONFIG[g]?.color}`} />
                                            <span className="capitalize">{g}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Total activos: {filtered.personal_por_compania.reduce((s,c) => s+c.activos, 0)}</span>
                                    <span>Total registrados: {filtered.personal_por_compania.reduce((s,c) => s+c.total, 0)}</span>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Asistencia Capacitaciones */}
                    {isVisible('asistencia_cap') && filtered?.asistencia_capacitaciones_por_compania && (
                        <Card className="border-2" data-testid="widget-asistencia-cap">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <GraduationCap className="h-4 w-4" /> Asistencia Capacitaciones
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline">General: {totales.asistencia_general_porcentaje || 0}%</Badge>
                                        <WidgetExportBtn onExport={() => exportSingleWidget('Asist.Capacitaciones',
                                            ['Compañía','Registros','Presentes','%'],
                                            filtered.asistencia_capacitaciones_por_compania.map(c => [c.compania_nombre,c.registros_asistencia,c.presentes,c.porcentaje])
                                        )} />
                                    </div>
                                </div>
                                <CardDescription>% de asistencia por compañía en capacitaciones</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {filtered.asistencia_capacitaciones_por_compania.map(cia => (
                                    <div key={cia.compania_id} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{cia.compania_nombre}</span>
                                            <span className="font-mono font-medium">
                                                {cia.porcentaje}%
                                                <span className="text-xs text-muted-foreground ml-1">({cia.presentes}/{cia.registros_asistencia})</span>
                                            </span>
                                        </div>
                                        <Progress value={cia.porcentaje} className="h-2" />
                                    </div>
                                ))}
                                {filtered.asistencia_capacitaciones_por_compania.every(c => c.registros_asistencia === 0) && (
                                    <p className="text-center text-sm text-muted-foreground py-4">Sin registros de asistencia aún</p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Asistencia Actas */}
                    {isVisible('asistencia_actas') && filtered?.asistencia_actas_por_compania && (
                        <Card className="border-2" data-testid="widget-asistencia-actas">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <FileText className="h-4 w-4" /> Asistencia Actas/Actividades
                                    </CardTitle>
                                    <WidgetExportBtn onExport={() => exportSingleWidget('Asist.Actas',
                                        ['Compañía','Actas','Posibles','Presentes','%'],
                                        filtered.asistencia_actas_por_compania.map(c => [c.compania_nombre,c.total_actas,c.total_posibles,c.total_presentes,c.porcentaje])
                                    )} />
                                </div>
                                <CardDescription>% presentes en actas de actividad por compañía</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {filtered.asistencia_actas_por_compania.map(cia => (
                                    <div key={cia.compania_id} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{cia.compania_nombre}</span>
                                            <span className="font-mono font-medium">
                                                {cia.porcentaje}%
                                                <span className="text-xs text-muted-foreground ml-1">({cia.total_actas} actas)</span>
                                            </span>
                                        </div>
                                        <Progress value={cia.porcentaje} className="h-2" />
                                    </div>
                                ))}
                                {filtered.asistencia_actas_por_compania.every(c => c.total_actas === 0) && (
                                    <p className="text-center text-sm text-muted-foreground py-4">Sin actas registradas aún</p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* EPP por Estado */}
                    {isVisible('epp_estado') && filtered?.epp_por_estado && (
                        <Card className="border-2" data-testid="widget-epp-estado">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2"><HardHat className="h-4 w-4" /> EPP por Estado</CardTitle>
                                    <Button variant="ghost" size="sm" onClick={() => navigate('/bomberos/epp')}>
                                        Ver EPP <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-3">
                                    {Object.entries(filtered.epp_por_estado).map(([estado, count]) => {
                                        const colorMap = {
                                            disponible: 'bg-green-100 text-green-700 border-green-200',
                                            asignado: 'bg-blue-100 text-blue-700 border-blue-200',
                                            mantenimiento: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                                            baja: 'bg-red-100 text-red-700 border-red-200',
                                            pendiente_baja: 'bg-orange-100 text-orange-700 border-orange-200',
                                        };
                                        return (
                                            <div key={estado} className={`p-3 rounded-lg border ${colorMap[estado] || 'bg-muted text-foreground'}`}>
                                                <p className="text-2xl font-bold">{count}</p>
                                                <p className="text-xs capitalize">{estado.replace('_', ' ')}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    )}


                    {/* Vencimientos EPP */}
                    {isVisible('epp_vencimientos') && eppVencimientos && (
                        <Card className="border-2" data-testid="widget-epp-vencimientos">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <ShieldAlert className="h-4 w-4" /> Vencimientos EPP
                                        {(eppVencimientos.resumen.vencidos + eppVencimientos.resumen.criticos_7d) > 0 && (
                                            <Badge className="bg-red-500 text-white ml-1">
                                                {eppVencimientos.resumen.vencidos + eppVencimientos.resumen.criticos_7d} urgentes
                                            </Badge>
                                        )}
                                    </CardTitle>
                                    <Button variant="ghost" size="sm" onClick={() => navigate('/bomberos/epp')}>
                                        Ver EPP <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Indicadores */}
                                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                                    <div className="p-2.5 rounded-lg border bg-red-100 text-red-700 border-red-200 text-center">
                                        <p className="text-xl font-bold">{eppVencimientos.resumen.vencidos}</p>
                                        <p className="text-[10px] font-medium">Vencidos</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg border bg-orange-100 text-orange-700 border-orange-200 text-center">
                                        <p className="text-xl font-bold">{eppVencimientos.resumen.criticos_7d}</p>
                                        <p className="text-[10px] font-medium">7 días</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg border bg-yellow-100 text-yellow-700 border-yellow-200 text-center">
                                        <p className="text-xl font-bold">{eppVencimientos.resumen.proximos_30d}</p>
                                        <p className="text-[10px] font-medium">30 días</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg border bg-blue-100 text-blue-700 border-blue-200 text-center">
                                        <p className="text-xl font-bold">{eppVencimientos.resumen.atencion_60d}</p>
                                        <p className="text-[10px] font-medium">60 días</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg border bg-green-100 text-green-700 border-green-200 text-center">
                                        <p className="text-xl font-bold">{eppVencimientos.resumen.vigentes}</p>
                                        <p className="text-[10px] font-medium">Vigentes</p>
                                    </div>
                                </div>

                                {/* Lista detallada de urgentes */}
                                {(eppVencimientos.vencidos.length > 0 || eppVencimientos.criticos.length > 0) && (
                                    <div className="space-y-1.5">
                                        <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wider">Requieren acción inmediata</h4>
                                        {[...eppVencimientos.vencidos, ...eppVencimientos.criticos].slice(0, 8).map(item => (
                                            <div key={item.id} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <HardHat className={`h-4 w-4 shrink-0 ${item.dias_restantes < 0 ? 'text-red-500' : 'text-orange-500'}`} />
                                                    <div className="min-w-0">
                                                        <span className="font-medium capitalize">{(item.tipo || '').replace('_', ' ')}</span>
                                                        <span className="text-muted-foreground ml-1.5 text-xs">{item.codigo}</span>
                                                        {item.bombero_nombre && (
                                                            <p className="text-xs text-muted-foreground truncate">{item.bombero_nombre} · {item.compania_nombre}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <Badge variant={item.dias_restantes < 0 ? 'destructive' : 'outline'}
                                                    className={item.dias_restantes < 0 ? '' : 'text-orange-600 border-orange-300'}>
                                                    {item.dias_restantes < 0
                                                        ? `Venció hace ${Math.abs(item.dias_restantes)}d`
                                                        : `${item.dias_restantes}d restantes`}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Próximos a vencer */}
                                {eppVencimientos.proximos.length > 0 && (
                                    <div className="space-y-1.5">
                                        <h4 className="text-xs font-semibold text-yellow-600 uppercase tracking-wider">Próximos a vencer (30 días)</h4>
                                        {eppVencimientos.proximos.slice(0, 5).map(item => (
                                            <div key={item.id} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                                                <div className="flex items-center gap-2">
                                                    <HardHat className="h-4 w-4 text-yellow-500 shrink-0" />
                                                    <span className="font-medium capitalize">{(item.tipo || '').replace('_', ' ')}</span>
                                                    <span className="text-xs text-muted-foreground">{item.codigo}</span>
                                                </div>
                                                <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                                                    {item.dias_restantes}d
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Sin fecha */}
                                {eppVencimientos.resumen.sin_fecha > 0 && (
                                    <p className="text-xs text-muted-foreground text-center pt-2 border-t">
                                        {eppVencimientos.resumen.sin_fecha} items sin fecha de vencimiento registrada
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* EPP por Compañía */}
                    {isVisible('epp_cia') && filtered?.epp_por_compania && (
                        <Card className="border-2" data-testid="widget-epp-cia">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2"><HardHat className="h-4 w-4" /> EPP por Compañía</CardTitle>
                                    <WidgetExportBtn onExport={() => exportSingleWidget('EPP_Compañia',
                                        ['Compañía','Total','Asignado','Disponible','Mantenimiento','Baja'],
                                        filtered.epp_por_compania.map(c => [c.compania_nombre,c.total,c.asignado,c.disponible,c.mantenimiento,c.baja])
                                    )} />
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {filtered.epp_por_compania.map(cia => (
                                    <div key={cia.compania_id} className="flex items-center gap-3 text-sm">
                                        <span className="w-28 truncate text-muted-foreground">{cia.compania_nombre}</span>
                                        <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                                            <div className="h-full bg-orange-500 rounded-full transition-all duration-500"
                                                style={{ width: `${Math.max((cia.total / Math.max(...filtered.epp_por_compania.map(c => c.total), 1)) * 100, 2)}%` }} />
                                        </div>
                                        <span className="w-16 text-right font-mono font-medium">{cia.total}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Material Mayor */}
                    {isVisible('material_mayor') && filtered?.material_mayor_por_compania && (
                        <Card className="border-2" data-testid="widget-material-mayor">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2"><Truck className="h-4 w-4" /> Material Mayor por Compañía</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {filtered.material_mayor_por_compania.map(cia => (
                                    <div key={cia.compania_id} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{cia.compania_nombre}</span>
                                            <span className="font-mono font-medium">
                                                {cia.operativos}/{cia.total}
                                                <span className="text-xs text-muted-foreground ml-1">({cia.porcentaje_operatividad}%)</span>
                                            </span>
                                        </div>
                                        <Progress value={cia.porcentaje_operatividad} className="h-2" />
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Capacitaciones por Estado */}
                    {isVisible('caps_estado') && filtered?.capacitaciones_por_estado && (
                        <Card className="border-2" data-testid="widget-caps-estado">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Capacitaciones por Estado</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-3">
                                    {Object.entries(filtered.capacitaciones_por_estado).map(([estado, count]) => {
                                        const colorMap = {
                                            programada: 'bg-blue-100 text-blue-700 border-blue-200',
                                            en_curso: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                                            finalizada: 'bg-green-100 text-green-700 border-green-200',
                                            cancelada: 'bg-red-100 text-red-700 border-red-200',
                                        };
                                        return (
                                            <div key={estado} className={`p-3 rounded-lg border ${colorMap[estado] || 'bg-muted'}`}>
                                                <p className="text-2xl font-bold">{count}</p>
                                                <p className="text-xs capitalize">{estado.replace('_', ' ')}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Competencias */}
                    {isVisible('competencias_cia') && filtered?.competencias_por_compania && (
                        <Card className="border-2" data-testid="widget-competencias-cia">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" /> Cobertura de Competencias</CardTitle>
                                <CardDescription>Bomberos activos con al menos 1 competencia</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {filtered.competencias_por_compania.map(cia => (
                                    <div key={cia.compania_id} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{cia.compania_nombre}</span>
                                            <span className="font-mono font-medium">
                                                {cia.porcentaje_cobertura}%
                                                <span className="text-xs text-muted-foreground ml-1">({cia.con_competencias}/{cia.bomberos_activos})</span>
                                            </span>
                                        </div>
                                        <Progress value={cia.porcentaje_cobertura} className="h-2" />
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Compañías */}
                    {isVisible('companias') && (
                        <Card className="border-2" data-testid="widget-companias">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Compañías</CardTitle>
                                    <Button variant="ghost" size="sm" onClick={() => navigate('/bomberos/companias')}>
                                        Ver todas <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {companias.length === 0 ? (
                                    <p className="text-center text-muted-foreground py-4">No hay compañías registradas</p>
                                ) : (
                                    <div className="space-y-2">
                                        {companias.filter(c => !filterCia || c.id === filterCia).map(comp => (
                                            <div key={comp.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                                                onClick={() => navigate('/bomberos/companias')} data-testid={`compania-${comp.numero}`}>
                                                <div>
                                                    <p className="font-medium text-sm">{comp.nombre}</p>
                                                    <p className="text-xs text-muted-foreground">{comp.total_bomberos} bomberos · {comp.total_vehiculos} vehículos</p>
                                                </div>
                                                <Badge variant="outline">{comp.numero}a Cía</Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Acciones Rápidas */}
                {isVisible('acciones') && (
                    <Card className="border-2" data-testid="widget-acciones">
                        <CardHeader className="pb-3"><CardTitle className="text-base">Acciones Rápidas</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/personal/nuevo')}>
                                    <Users className="h-4 w-4" /><span className="text-xs">Nuevo Bombero</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/capacitaciones/nueva')}>
                                    <GraduationCap className="h-4 w-4" /><span className="text-xs">Nueva Capacitación</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/material-mayor/nuevo')}>
                                    <Truck className="h-4 w-4" /><span className="text-xs">Nuevo Vehículo</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/epp/nuevo')}>
                                    <HardHat className="h-4 w-4" /><span className="text-xs">Nuevo EPP</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/actas/nueva')}>
                                    <BarChart3 className="h-4 w-4" /><span className="text-xs">Nueva Acta</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/bomberos/reportes')}>
                                    <BarChart3 className="h-4 w-4" /><span className="text-xs">Reportes</span>
                                </Button>
                                <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 relative" onClick={() => navigate('/bomberos/validacion-bajas')}>
                                    <FileCheck className="h-4 w-4" /><span className="text-xs">Validar Bajas</span>
                                    {solicitudesBajaPendientes > 0 && (
                                        <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-orange-500">{solicitudesBajaPendientes}</Badge>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </>
    );
}
